import React, { useEffect, useState } from 'react';
import { CITY, COUNTY } from '@/user/user-profile/user-profile-constants';
import { strOracleApiSender } from '@/utils/stroracle-api-sender';
import styled from 'styled-components';
import { LoadingPicker } from './modules/loading-picker';

const AnyCityPickerWrapper = styled.div`
  width: 100%;
`;

/**
 * @typedef {Object} PlaceDetails
 * @property {string} state Two-letter uppercase state
 * @property {'CITY' | 'COUNTY'} placeType Whether the place is a city or a county
 * @property {string} place Place ID string (e.g. `ca-san_mateo`)
 */
/**
 * Advanced picker for cities and counties
 *
 * @param {string} props.cityOrCounty tells if user wants to select cities or counties
 * @param {PlaceDetails} selectedPlace Details of the currently-selected place in the picker
 * @param {(newDetails: PlaceDetails) => void} setSelectedPlace Callback to update place when
 * user picks a new place in the dropdown or clicks on the Imperial Beach Demo button
 */
export function AnyCityPicker({
  cityOrCounty,
  setSelectedPlace,
  selectedPlace,
}) {
  const [stateCountyInfo, setStateCountyInfo] = useState(); // { stateDDOptions, countiesByState }
  const [cityDDOptions, setCityDDOptions] = useState();
  const [countyDDOptions, setCountyDDOptions] = useState();

  useEffect(() => {
    (async function fetchStatesAndCounties() {
      const { data } = await strOracleApiSender.rawGet('counties');
      const stateOptions = Object.keys(data)
        .map((state) => ({ value: state, label: state }))
        .sort((a, b) => a.label.localeCompare(b.label));
      const statesAndCountiesByState = {
        stateDDOptions: stateOptions,
        countiesByState: data,
      };
      setStateCountyInfo(statesAndCountiesByState);
    })();
  }, []);

  useEffect(() => {
    const countiesByState = stateCountyInfo?.countiesByState;
    const selectedState = selectedPlace?.state;
    if (cityOrCounty === COUNTY && selectedState && countiesByState) {
      const countiesForCurrentState = countiesByState[selectedState];
      const countyOpts = countiesForCurrentState?.map(({ county, place }) => ({
        value: place,
        placeDetails: { state: selectedState, place, placeType: COUNTY },
        label: county,
      }));
      setCountyDDOptions(countyOpts);
    } else {
      (async function fetchCities() {
        if (selectedState) {
          const { data } = await strOracleApiSender.rawGet(
            `${selectedState}/cities`
          );
          const cities = Object.keys(data);
          const cityOptions = cities.map((city) => ({
            value: data[city].place,
            placeDetails: {
              state: selectedState,
              place: data[city].place,
              placeType: CITY,
            },
            label: city,
          }));
          setCityDDOptions(cityOptions);
        }
      })();
    }
  }, [cityOrCounty, selectedPlace, setCityDDOptions, stateCountyInfo]);

  const placeDDOptions =
    cityOrCounty === COUNTY ? countyDDOptions : cityDDOptions;

  return (
    <AnyCityPickerWrapper>
      <LoadingPicker
        dataTestId="admin-dashboard-state-picker"
        placeholder="Select State"
        cleanable={false}
        value={selectedPlace?.state}
        loading={!stateCountyInfo}
        onSelect={(state) => {
          setCityDDOptions(null);
          setSelectedPlace({
            ...selectedPlace,
            state,
            place: selectedPlace.state !== state ? null : selectedPlace.place,
          });
        }}
        data={stateCountyInfo?.stateDDOptions}
      />

      <LoadingPicker
        dataTestId="admin-dashboard-place-picker"
        placeholder="Select Place"
        cleanable={false}
        value={selectedPlace?.place}
        hidden={!selectedPlace?.state || !stateCountyInfo}
        loading={selectedPlace?.state && !placeDDOptions}
        onSelect={(place, selectedOption) => {
          setSelectedPlace(selectedOption.placeDetails);
        }}
        data={placeDDOptions}
      />
    </AnyCityPickerWrapper>
  );
}
